import { useState, useMemo } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import styled from 'styled-components';
import { ControleKubusClient, ReviewClient, ReviewedRegelsDto, ReviewedRegelDto, StatusEnum, ControleKubus, KleurStatusEnum }
  from '../client/procescontroleclient';
import Table from "../components/tables/Table";
import CheckBox from '../components/UI/CheckBox';
import Button from "../components/UI/Button";
import Refresh from "../components/UI/Refresh";
import PageTitle from '../components/UI/PageTitle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import themeColors from '../config/themeColors';
import ButtonWrap, { MobileButtonWrap } from '../components/UI/ButtonWrap';
import Input from "../components/UI/Input";
import { useMediaQuery } from 'react-responsive';
import ProcesControleMobileCard, { Row, Item, Label, Field } from "../components/UI/ProcesControleMobileCard";
import { ButtonType } from "../Types";
import Spinner from "../components/UI/Spinner";
import PageWrapper from '../components/UI/PageWrapper';
import { getDateTimeFormat } from "../utilities/Utilities";
import { InputType, TableType } from '../Types';
import useDebounce from "../hooks/useDebounce";
import StuksIndicatie from '../components/UI/StuksIndicatie';

interface Props {
  color: KleurStatusEnum;
}

const Tr = styled.tr<Props>`
  ${props => props.color===KleurStatusEnum.Red ? 'background-color: rgba(235,100,96,0.5) !important;' : null}; 
  ${props => props.color===KleurStatusEnum.Orange ? `background-color: rgb(235 157 110 / 60%) !important;` : null};
`;

export default function ProcesControleKubus() {
  const [selectedRows, setSelectedRows] = useState(Array<number | undefined>);
  const [filter, setFilter] = useState<string>('');
  const kubusClient = new ControleKubusClient(process.env.REACT_APP_APIURL);
  const opgelostClient = new ReviewClient(process.env.REACT_APP_APIURL);
  const isMobile = useMediaQuery({ query: '(max-width: 767px)' });
  const debouncedFilter = useDebounce(filter, 300);

  const checkFilterRow: any = (filterRow: ControleKubus) => {

    if (debouncedFilter === '') return filterRow;
    const filterableKeys = ['omschrijving', 'debiteurnummer', 'picker', 'pickLocatie', 'laan'];
    return filterableKeys.some((key) => (filterRow[key as keyof ControleKubus] as string).toLowerCase().includes(debouncedFilter.toLowerCase()));
  }

  const kubusQuery = useQuery(['controleKubus', 'all'], async () => {
    return await kubusClient.get();
  }, {
    staleTime: Infinity
  });

  const filteredKubusData = useMemo(() => {

    if (kubusQuery.data) {
      return kubusQuery.data.filter(checkFilterRow);
    }
    return [];
  }, [kubusQuery.data, debouncedFilter]);

  const createReviewedRowsMutation = useMutation(async (reviewedRows: ReviewedRegelsDto) => {
    return await opgelostClient.create(reviewedRows);
  }, {
    onSuccess: () => {
      kubusQuery.refetch();
      setSelectedRows([]);
    }
  });

  const checkBoxHandler = (barCode: number | undefined) => {
    if (selectedRows.includes(barCode)) {
      setSelectedRows(selectedRows.filter((item) => item !== barCode));
    } else {
      setSelectedRows([...selectedRows, barCode]);
    }
  }

  const statusHandler = (status: StatusEnum) => {
    const reviewedDto = new ReviewedRegelsDto();
    reviewedDto.status = status;

    reviewedDto.reviewedOrderProcesControleRows = selectedRows.map((barcode) => {
      const reviewedRow = new ReviewedRegelDto();
      var row = kubusQuery.data?.find((item) => item.barcode === barcode);
      if (row !== undefined) {
        reviewedRow.barcode = row.barcode;
        reviewedRow.sourceCode = row.sourceCode;
        reviewedRow.orderStatus = row.orderStatus;
      }
      return reviewedRow;
    });

    createReviewedRowsMutation.mutate(reviewedDto);

    return;
  }

  if (kubusQuery.isLoading || kubusQuery.isFetching) {
    return <Spinner />;
  }

  return (
    <PageWrapper>
      <PageTitle>Kubus controle</PageTitle>
      <ButtonWrap>
        {!isMobile ? <Button buttonType={ButtonType.Primary} spacingR svg onClick={() => kubusQuery.refetch()}><FontAwesomeIcon icon="eye" />Opvragen</Button>
          : <Refresh onClick={() => kubusQuery.refetch()} />}
        <MobileButtonWrap>
          <Input
            inputType={InputType.SearchInput}
            onChange={(e: React.FormEvent<HTMLInputElement>) => setFilter(e.currentTarget.value)}
            value={filter}
          />
          <Button disabled={createReviewedRowsMutation.isLoading || selectedRows.length == 0} buttonType={ButtonType.Cancel} spacingR onClick={() => statusHandler(StatusEnum.Opgelost)}
            indicator={selectedRows.length.toString()} color={themeColors.ftgPastelGreen}>Staat in laan </Button>
          <Button disabled={createReviewedRowsMutation.isLoading || selectedRows.length == 0} buttonType={ButtonType.Secondary} spacingR onClick={() => statusHandler(StatusEnum.Openstaand)}
            indicator={selectedRows.length.toString()} color={themeColors.ftgBlue}>Staat niet in laan</Button>
          <Button disabled={createReviewedRowsMutation.isLoading || selectedRows.length == 0} buttonType={ButtonType.Secondary} onClick={() => statusHandler(StatusEnum.UitgeslotenVoorKubus)}
            indicator={selectedRows.length.toString()} color={themeColors.ftgGreen}>Uitsluiten voor Kubus</Button>
        </MobileButtonWrap>
      </ButtonWrap>
      {!isMobile ?
        <Table tableType={TableType.Default} stickymodal>
          <thead>
            <tr>
              <th>Debnr</th>
              <th>Omschrijving</th>
              <th style={{ textAlign: 'right' }}>Lengte</th>
              <th>VPartijnummer</th>
              <th style={{ textAlign: 'right' }}>Stuks totaal</th>
              <th style={{ textAlign: 'right' }}>Stuks afgerond</th>
              <th>Locatie</th>
              <th>Laan</th>
              <th style={{ textAlign: 'left' }}>Picker</th>
              <th style={{ textAlign: 'left' }}>Printtijd</th>
              <th style={{ textAlign: 'left' }}>Laatst geleegd</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filteredKubusData.map((row, index) => {
              const scanLastKubusEmptied = getDateTimeFormat(row.scanLastKubusEmptied);
              const isEmptied = scanLastKubusEmptied !== "";
              return (
                <Tr key={index} color={isEmptied ? KleurStatusEnum.Orange : row.businessRuleAfgegaan ? KleurStatusEnum.Red : KleurStatusEnum.Empty}>
                  <td>{row.debiteurnummer}</td>
                  <td>{row.omschrijving}</td>
                  <td style={{ textAlign: 'right' }}>{row.lengte}</td>
                  <td>{row.vPartijnummer}</td>
                  <td style={{ textAlign: 'right' }}>{row.stuksTotaal}</td>
                  <td style={{ textAlign: 'right' }}>{row.stuksAfgerond}</td>
                  <td>{row.pickLocatie}</td>
                  <td>{row.laan}</td>
                  <td>{row.picker}</td>
                  <td>
                    {getDateTimeFormat(row.pickTijd)}
                  </td>
                  <td>
                    {isEmptied ? scanLastKubusEmptied : ""}
                  </td>
                  <td>
                    <CheckBox
                      checked={selectedRows.includes(row.barcode)}
                      onClick={() => checkBoxHandler(row.barcode)}
                    />
                  </td>
                </Tr>
              )
            })}
          </tbody>
        </Table> :
        filteredKubusData.map((row, index) => {
          const scanLastKubusEmptied = getDateTimeFormat(row.scanLastKubusEmptied);
          const isEmptied = scanLastKubusEmptied !== "";
          let color = KleurStatusEnum.Empty
          color = row.businessRuleAfgegaan ? KleurStatusEnum.Red : color;
          color = isEmptied ? KleurStatusEnum.Orange : color;

          return (
            <ProcesControleMobileCard color={color}
              compactView={
                <>
                  <div><strong>Laan</strong>{row.laan}</div>
                  <div><strong>Aant.</strong>{row.aantal}</div>
                  <StuksIndicatie afgerond={row.stuksAfgerond} totaal={row.stuksTotaal} />
                </>
              }
              expandView={
                <>
                  <Row>
                    <Item><Label>Omschrijving</Label><Field>{row.omschrijving}</Field></Item>
                  </Row>
                  <Row>
                    <Item>
                      <Label>Stuks totaal</Label><Field>{row.stuksTotaal}</Field>
                    </Item>
                    <Item>
                      <Label>Stuks afgerond</Label><Field>{row.stuksAfgerond}</Field>
                    </Item>
                  </Row>
                  <Row>
                    <Item>
                      <Label>Picker</Label><Field>{row.picker}</Field>
                    </Item>
                    <Item>
                      <Label>Printtijd</Label><Field>{getDateTimeFormat(row.pickTijd)}</Field>
                    </Item>
                  </Row>
                  <Row>
                    <Item>
                      <Label>vPartijnummer</Label><Field>{row.vPartijnummer}</Field>
                    </Item>
                  </Row>
                  <Row>
                    <Item>
                      <Label>Lengte</Label><Field>{row.lengte}</Field>
                    </Item>
                  </Row>
                  <Row>
                    <Item>
                      <Label>Debnr.</Label><Field>{row.debiteurnummer}</Field>
                    </Item>
                    <Item>
                      <Label>Locatie</Label><Field>{row.pickLocatie}</Field>
                    </Item>
                    <Item>
                      <Label>Laan</Label><Field>{row.laan}</Field>
                    </Item>
                  </Row>
                  {
                    isEmptied ? <Row>
                      <Item><Label>Laatst geleegd</Label><Field>{isEmptied ? scanLastKubusEmptied : ""}</Field></Item>
                    </Row> : null
                  }
                </>
              }
              checked={selectedRows.includes(row.barcode!)}
              onclick={() => checkBoxHandler(row.barcode!)}
              title={row.omschrijving}
            />
          )
        })}
    </PageWrapper>
  );
}